import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle2, Award, AlertCircle, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const AssessmentResults = ({ results, dimensions, onClose, onComplete, onPrint, onReset, reportRef, getScoreColor, getScoreBg, getScoreLabel }) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 print:bg-white print:p-0">
      <div
        ref={reportRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden print:max-w-none print:max-h-none print:shadow-none print:rounded-none"
        data-testid="assessment-results"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 text-white print:bg-emerald-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{t('assessment.results.title', 'Your 5D Assessment Results')}</h2>
                <p className="text-emerald-100 text-sm">{t('assessment.results.subtitle', 'Personalized coaching insights')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] print:max-h-none print:overflow-visible">
          {/* Overall Score */}
          <div className="text-center mb-8 pb-6 border-b">
            <p className="text-sm text-gray-500 mb-2">{t('assessment.results.overallScore', 'Overall Readiness Score')}</p>
            <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full ${getScoreBg(results.overallScore)}`}>
              <div className="text-center">
                <p className={`text-4xl font-bold ${getScoreColor(results.overallScore)}`}>{results.overallScore}%</p>
                <p className={`text-sm font-medium ${getScoreColor(results.overallScore)}`}>
                  {getScoreLabel(results.overallScore)}
                </p>
              </div>
            </div>
          </div>

          {/* Dimension Scores */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('assessment.results.dimensionScores', '5D Dimension Scores')}
            </h3>
            <div className="space-y-4">
              {Object.entries(dimensions).map(([key, dim]) => {
                const score = results.dimensionScores[key];
                const DimIcon = dim.icon;
                return (
                  <div key={key} className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${dim.gradient} flex items-center justify-center flex-shrink-0`}>
                      <DimIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-700">{dim.name}</span>
                        <span className={`font-bold ${getScoreColor(score)}`}>{score}%</span>
                      </div>
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${dim.gradient} rounded-full transition-all duration-500`}
                          style={{ width: `${score}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{dim.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Priority Areas */}
          {results.areasNeedingSupport.length > 0 && (
            <div className="mb-8 p-5 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-red-900">
                  {t('assessment.results.areasNeedingSupport', 'Priority Areas for Coaching')}
                </h3>
              </div>
              <p className="text-sm text-red-700 mb-4">
                {t('assessment.results.areasNeedingSupportDesc', 'These areas could benefit most from focused coaching support:')}
              </p>
              <div className="space-y-3">
                {results.areasNeedingSupport.map(({ dimension, score }) => {
                  const dim = dimensions[dimension];
                  const DimIcon = dim.icon;
                  return (
                    <div key={dimension} className="flex items-center justify-between bg-white p-3 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${dim.gradient} flex items-center justify-center`}>
                          <DimIcon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{dim.name}</p>
                          <p className="text-xs text-gray-500">{dim.description}</p>
                        </div>
                      </div>
                      <span className="text-red-600 font-bold">{score}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Strengths */}
          {results.strengths.length > 0 && (
            <div className="mb-8 p-5 bg-green-50 rounded-xl border border-green-200">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold text-green-900">
                  {t('assessment.results.strengths', 'Your Strengths')}
                </h3>
              </div>
              <p className="text-sm text-green-700 mb-4">
                {t('assessment.results.strengthsDesc', 'These areas show strong foundations to build upon:')}
              </p>
              <div className="flex flex-wrap gap-3">
                {results.strengths.map(({ dimension, score }) => {
                  const dim = dimensions[dimension];
                  const DimIcon = dim.icon;
                  return (
                    <div key={dimension} className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-green-300">
                      <DimIcon className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-800">{dim.name}</span>
                      <span className="text-green-600 font-bold">{score}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recommendations */}
          <div className="mb-8 p-5 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-900">
                {t('assessment.results.recommendations', 'Personalized Recommendations')}
              </h3>
            </div>
            <ul className="space-y-2 text-sm text-blue-800">
              {results.areasNeedingSupport.length > 0 ? (
                <>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{t('assessment.results.rec1')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{t('assessment.results.rec2')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{t('assessment.results.rec3')}</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{t('assessment.results.rec4')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{t('assessment.results.rec5')}</span>
                  </li>
                </>
              )}
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{t('assessment.results.rec6')}</span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 print:hidden">
            <Button
              onClick={() => {
                onClose();
                if (onComplete) onComplete(results);
              }}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 py-6 text-lg"
            >
              {t('assessment.results.findCoach', 'Find a Coach to Help')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              onClick={onReset}
              className="flex-1 py-6 text-lg"
            >
              {t('assessment.results.retake', 'Retake Assessment')}
            </Button>
          </div>

          {/* Print Footer */}
          <div className="hidden print:block mt-8 pt-4 border-t text-center text-sm text-gray-500">
            <p>CoachMeUpp 5D Assessment Report</p>
            <p>Generated on {new Date().toLocaleDateString()}</p>
            <p className="mt-2">www.coachmeupp.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentResults;
